import { RoleCode } from "../../permissions/permissions.js";
import { resolveEffectivePermissions } from "../../permissions/effective-permissions.js";
import type { BuiltInRoleCode } from "../../domain/identity/access-types.js";

export interface FrontendContext {
  isAuthenticated: boolean;
  activeClientId?: string | null | undefined;
  activeClientName?: string | undefined;
  permissions: Set<string>;
  userId: string;
  authenticatedUserId?: string | undefined;
  professionalId?: string | null | undefined;
  userName: string;
  userEmail?: string | undefined;
  role: string;
  roleDisplay: string;
}

// Role display name translation catalog (presentation layer only)
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  DEVELOPER: "DESENVOLVEDOR",
  ADMIN: "ADMINISTRADOR",
  ENGINEER: "ENGENHEIRO",
  INSPECTOR: "INSPETOR",
  DOCUMENTATION: "TÉCNICO DE DOCUMENTAÇÃO",
  DOCUMENT_TECH: "TÉCNICO DE DOCUMENTAÇÃO",
  TECHNICIAN: "INSPETOR",
  // M16A Etapa 7: corrigir label MANAGER de "ADMINISTRADOR" para "GERENTE".
  MANAGER: "GERENTE",
  VIEWER: "VISUALIZADOR"
};

export function getRoleDisplayName(roleCode?: string | null): string {
  if (!roleCode) return "Desconhecido";
  return ROLE_DISPLAY_NAMES[roleCode.toUpperCase()] || roleCode || "Desconhecido";
}

/**
 * IDENTIDADE NUNCA VEM DO localStorage.
 *
 * Quem sou eu, qual o meu perfil e quais as minhas permissões são decididos
 * na origem (servidor em homologação, repositório de Usuários no teste local)
 * e revalidados a cada carga da página por `authGateway.restoreSession()` em
 * `main.ts`, que então chama `login()`. O contexto abaixo vive só em memória:
 * um F5 parte de "não autenticado" até a revalidação terminar.
 *
 * Isso é deliberado. Enquanto userId/role/permissões eram relidos de chaves
 * do navegador (`inspect_active_role`, `inspect_user_custom_permissions`...),
 * qualquer pessoa com o devtools aberto escrevia `ADMIN` ali e recarregava.
 * A única coisa que continua no armazenamento do navegador é o TOKEN da
 * sessão (responsabilidade do `auth-gateway`) e o Cliente ativo — uma
 * seleção de trabalho, sempre re-verificada no servidor.
 */
const savedClientId = (typeof localStorage !== 'undefined' && localStorage.getItem('inspect_active_client_id')) || null;
const savedClientName = (typeof localStorage !== 'undefined' && localStorage.getItem('inspect_active_client_name')) || null;

let currentContext: FrontendContext = {
  isAuthenticated: false,
  activeClientId: savedClientId,
  activeClientName: savedClientName || "",
  permissions: new Set<string>(),
  userId: "",
  authenticatedUserId: "",
  userName: "",
  userEmail: "",
  professionalId: null,
  role: "",
  roleDisplay: ""
};

type ContextListener = (ctx: FrontendContext) => void;
const listeners: Set<ContextListener> = new Set();

export const contextStore = {
  get(): FrontendContext {
    // Sem re-hidratação a partir do navegador: se a sessão ainda não foi
    // validada na origem, o contexto é o de alguém não autenticado.
    return currentContext;
  },
  
  set(newContext: Partial<FrontendContext>) {
    currentContext = { ...currentContext, ...newContext };
    
    if (typeof localStorage !== 'undefined') {
      if (currentContext.activeClientId) {
        localStorage.setItem('inspect_active_client_id', currentContext.activeClientId);
      } else {
        localStorage.removeItem('inspect_active_client_id');
      }
      if (currentContext.activeClientName) {
        localStorage.setItem('inspect_active_client_name', currentContext.activeClientName);
      } else {
        localStorage.removeItem('inspect_active_client_name');
      }
      // userId / role / professionalId / permissões NÃO são gravados: vêm da
      // origem a cada carga. Ver o comentário no topo do arquivo.
    }

    for (const listener of listeners) {
      try {
        listener(currentContext);
      } catch (e) {
        console.error("Context listener error", e);
      }
    }
  },

  // TROCAR DE USUÁRIO = NOVA AUTENTICAÇÃO.
  //
  // `switchToUser()` e `switchRole()` foram REMOVIDOS. Eles assumiam a conta
  // de outra pessoa (nome, perfil, permissões e Professional) sem pedir
  // senha: bastava um administrador escolher "JOÃO" numa lista. Quem precisa
  // trabalhar como outro usuário faz logout e entra com as credenciais dele
  // — ver `requireReauthentication()` abaixo, usado pelo topbar.

  /** Encerra a sessão atual e leva à tela de acesso. Não há atalho sem senha. */
  requireReauthentication(): void {
    this.logout();
  },

  hasPermission(permission: string): boolean {
    // No hardcoded role-name bypass: ADMIN's access to every permission
    // comes from BUILT_IN_ROLE_PERMISSIONS[RoleCode.Admin] (which already
    // grants every PermissionCode) via resolveEffectivePermissions() above
    // and in login()/switchToUser() — the same resolution path every other
    // role goes through.
    return currentContext.permissions.has(permission);
  },

  hasRole(role: string): boolean {
    return currentContext.role.toUpperCase() === role.toUpperCase();
  },

  // Bloco 02: os atalhos isInspector()/isTechnician()/isEngineer()/isAdmin()
  // foram REMOVIDOS. Autorização é sempre por PERMISSÃO (hasPermission), nunca
  // por nome de perfil — um "if (role === 'INSPECTOR')" espalhado pelo código
  // é exatamente o que o modelo de RBAC central existe para impedir.

  subscribe(listener: ContextListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  logout(): void {
    if (typeof localStorage !== 'undefined') {
      const token = localStorage.getItem('inspect_auth_token');
      fetch('/api/logout', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
        credentials: 'include',
        cache: 'no-store'
      }).catch(() => {});
      localStorage.removeItem('inspect_auth_token');
      // Registro da sessão do teste local — sem removê-lo, o token some mas
      // o vínculo token→usuário continua gravado.
      localStorage.removeItem('inspect_local_session');
      localStorage.removeItem('inspect_user_custom_permissions');
      localStorage.removeItem('inspect_user_name');
      localStorage.removeItem('inspect_user_id');
      localStorage.removeItem('inspect_authenticated_user_id');
      localStorage.removeItem('inspect_active_role');
      localStorage.removeItem('inspect_professional_id');
      localStorage.removeItem('inspect_active_client_id');
      localStorage.removeItem('inspect_active_client_name');
      localStorage.removeItem('inspect_user_email');
    }
    this.set({
      isAuthenticated: false,
      userId: "",
      authenticatedUserId: "",
      userName: "",
      userEmail: "",
      role: "",
      roleDisplay: "",
      activeClientId: null,
      activeClientName: "",
      permissions: new Set(),
      professionalId: null
    });
    // Force reload to apply new state correctly
    window.location.href = "/login";
  },
  
  /**
   * Login with real user data from the database.
   * This is the ONLY proper way to authenticate.
   */
  login(userName: string, userId: string, role: string, extra?: {
    userEmail?: string;
    professionalId?: string | null;
    activeClientId?: string | null;
    activeClientName?: string;
    customPermissions?: readonly string[];
  }): void {
    // Sem perfil padrão: um `role` vazio vindo da origem é um dado faltando,
    // e faltando dado o acesso é o de menor privilégio — nunca Administrador.
    const roleCode = (role || RoleCode.Viewer).toUpperCase();
    const permissions = resolveEffectivePermissions(roleCode, extra?.customPermissions);

    this.set({
      isAuthenticated: true,
      userId: userId,
      authenticatedUserId: userId,
      userName: userName,
      userEmail: extra?.userEmail || "",
      role: roleCode,
      roleDisplay: getRoleDisplayName(roleCode),
      professionalId: extra?.professionalId ?? null,
      activeClientId: (extra?.activeClientId !== undefined ? extra.activeClientId : currentContext.activeClientId) ?? null,
      activeClientName: (extra?.activeClientName !== undefined ? extra.activeClientName : currentContext.activeClientName) || "",
      permissions
    });
  }
};

// Expose on window for runtime checks in services and components
if (typeof window !== 'undefined') {
  (window as any).contextStore = contextStore;
}
